<template>
    <div class="container-fluid">
        <Loading v-if="!loaded"></Loading>
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('drug_card') }}</h3>
            </div>
            <!-- DRUG CARD SEARCH PARAMETERS FORM -->
            <div class="row mt-3">
                <div class="col-sm-10">
                    <ValidationObserver ref="drugCardForm" v-slot="{ handleSubmit, invalid }">
                        <form role="form" @submit.prevent="handleSubmit(searchLogs)">
                            <div class="row">
                                <div class="col-sm-6">
                                    <!-- VNR -->
                                    <div class="form-group">
                                        <ValidationProvider
                                            name="VNR"
                                            :rules="{
                                                required: true,
                                                min: vnrLength,
                                                max: vnrLengthCustom,
                                                vnr_in_used_meds: { vnrNameMatch }
                                            }"
                                            v-slot="{ errors }"
                                        >
                                            <label for="vnr">{{ $t('vnr') }}</label>
                                            <input
                                                autocomplete="off"
                                                class="form-control"
                                                id="vnr"
                                                type="text"
                                                v-model="drug.vnr"
                                                v-on:keydown="clearVnrOptions"
                                                v-on:keyup="getMedicationName"
                                                :placeholder="searchingVnrs ? $t('searching') : ''"
                                            />
                                            <ul v-if="vnrOptions && vnrOptions.length > 0" class="custom_ul">
                                                <li
                                                    v-for="item in vnrOptions"
                                                    v-bind:key="item.vnr"
                                                    v-on:click="chooseVnr(item.vnr)"
                                                >
                                                    <span>{{ getMedDisplayName(item, true, true) }}</span>
                                                </li>
                                            </ul>
                                            <div class="invalid-feedback" style="display: block">
                                                <span>
                                                    {{ $t(errors[0]) }}
                                                    {{ errors[0] === 'error_too_short' ? vnrLength : null }}
                                                    {{ errors[0] === 'error_too_long' ? vnrLengthCustom : null }}
                                                </span>
                                                <span v-if="errors[0] == 'error_vnr_not_found'">
                                                      . {{ $t('error_vnr_not_found_try_to_get_from_list') }}
                                                </span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                    <!-- Medication name -->
                                    <div class="form-group">
                                        <ValidationProvider
                                            name="medName"
                                            :rules="{
                                                med_name_valid: { searchDone, vnrOptions }
                                            }"
                                            v-slot="{ errors }"
                                        >
                                            <label for="medication_name">{{ $t('medication_name') }}</label>
                                            <textarea
                                                type="text"
                                                id="medication_name"
                                                class="form-control"
                                                v-model="drug.showName"
                                                v-on:keyup="getMedicationVnrsDelay"
                                            />
                                            <div class="invalid-feedback" style="display: block">
                                                <span>
                                                    {{ $t(errors[0]) }}
                                                </span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <!-- From -->
                                    <div class="form-group">
                                        <label for="fromDate">{{ $t('from') }}</label>
                                        <input class="form-control" id="fromDate" type="date" v-model="fromDate" />
                                    </div>
                                    <!-- To -->
                                    <div class="form-group">
                                        <label for="toDate">{{ $t('to') }}</label>
                                        <input class="form-control" id="toDate" type="date" v-model="toDate" />
                                    </div>
                                </div>
                            </div>
                            <!-- BUTTONS -->
                            <div class="row form-group mt-3">
                                <div class="d-flex ml-3 mr-5">
                                    <input
                                        type="submit"
                                        class="btn btn-primary"
                                        :value="$t('search')"
                                        :disabled="invalid || !vnrNameMatch || submitClicked"
                                    />
                                </div>
                                <div class="d-flex mr-5">
                                    <button
                                        class="btn btn-secondary"
                                        v-on:click="resetFilters"
                                        :disabled="submitClicked"
                                    >
                                        {{ $t('reset') }}
                                    </button>
                                </div>
                                <div class="d-flex mr-5">
                                    <button
                                        type="button"
                                        class="btn btn-info"
                                        v-on:click="print"
                                        :disabled="!logsRetrieved || submitClicked"
                                    >
                                        {{ $t('print') }}
                                    </button>
                                </div>
                                <div class="btn-group" :disabled="submitClicked">
                                    <b-dropdown
                                        variant="info"
                                        id="drugCardLayoutDropdown"
                                        :text="$t('choose_layout')"
                                        offset="-320px;"
                                    >
                                        <div class="row drugcard_row">
                                            <div class="col-6">
                                                <div
                                                    :class="[
                                                        'drugcard_view_block',
                                                        !pharmacy.config.drug_card_layout
                                                            .individual_meds_separate_column
                                                            ? 'drugcard_chosen'
                                                            : ''
                                                    ]"
                                                    @click="handleMedsColumnLayoutUpdate(false)"
                                                >
                                                    <h5>{{ $t('view_individual_meds_same_column') }}</h5>
                                                    <img
                                                        class="drugcard_view_look align-bottom"
                                                        :src="getLayoutImg('same')"
                                                        alt=""
                                                    />
                                                </div>
                                            </div>
                                            <div class="col-6">
                                                <div
                                                    :class="[
                                                        'drugcard_view_block',
                                                        pharmacy.config.drug_card_layout.individual_meds_separate_column
                                                            ? 'drugcard_chosen'
                                                            : ''
                                                    ]"
                                                    @click="handleMedsColumnLayoutUpdate(true)"
                                                >
                                                    <h5>{{ $t('view_individual_meds_separate_column') }}</h5>
                                                    <img
                                                        class="drugcard_view_look align-bottom"
                                                        :src="getLayoutImg('separate')"
                                                        alt=""
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row drugcard_row mt-2">
                                            <div class="col-sm-6">
                                                <div class="d-flex ml-2">
                                                    <label for="showInventoryCheckColumn" v-b-tooltip.hover>
                                                        {{ $t('show_inventory_check_column') }}
                                                    </label>
                                                    <b-form-checkbox
                                                        id="showInventoryCheckColumn"
                                                        class="ml-2"
                                                        v-model="
                                                            pharmacy.config.drug_card_layout.show_inventory_check_column
                                                        "
                                                        switch
                                                        @change="updateDrugCardLayout"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </b-dropdown>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
            <!-- DRUG CARD TABLE -->
            <div class="row mt-3">
                <div class="col" v-if="logsRetrieved && logs.length">
                    <h3>{{ tableTitle }}</h3>
                    <!-- layout: full packages and individual pieces in separate columns -->
                    <DrugCardTableMedsSame
                        v-if="!pharmacy.config.drug_card_layout.individual_meds_separate_column"
                        v-bind:logs="logs"
                        v-bind:layout="pharmacy.config.drug_card_layout"
                        @tableFields="tableFields = $event"
                    />
                    <!-- layout: full packages and individual pieces in the same column -->
                    <DrugCardTableMedsSeparate
                        v-else
                        v-bind:logs="logs"
                        v-bind:layout="pharmacy.config.drug_card_layout"
                        @tableFields="tableFields = $event"
                    />
                </div>
                <!-- if there are no logs, show "no logs" text -->
                <div class="col" v-else-if="logsRetrieved">
                    <i>{{ noLogsString }}</i>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import {
    getDrugCard,
    getFilteredMedications,
    getPharmacy,
    handleAxiosError,
    updatePharmacy
} from '@/services/axiosService';
import { dateIsValid, getLocalDate, getLocalDateISOString } from '@/services/dateService';
import { getMedDisplayAmount, getMedDisplayName } from '@/services/medicationService';
import DrugCardTableMedsSame from './layouts/MedsSame';
import DrugCardTableMedsSeparate from './layouts/MedsSeparate';

export default {
    components: {
        Loading,
        DrugCardTableMedsSame,
        DrugCardTableMedsSeparate
    },
    data() {
        return {
            currentInventory: null,
            dateIsValid,
            drug: {
                company: '',
                dosage: '',
                dosage_form: '',
                form: '',
                name: '',
                package_size: '',
                showName: '',
                showNamePrevious: '',
                vnr: this.$route.params.vnr || ''
            },
            error: false,
            fromDate: '',
            getLocalDate,
            getMedDisplayAmount,
            getMedDisplayName,
            loaded: false,
            logs: [],
            logsRetrieved: false,
            pharmacy: null,
            searchDone: false,
            searchingVnrs: false,
            searchParams: {
                searchFrom: '',
                searchTo: ''
            },
            searchVnrDelay: 500,
            searchVnrTimer: null,
            submitClicked: false,
            success: false,
            tableFields: {},
            tableTitle: '',
            toDate: '',
            vnrLength: 6,
            vnrLengthCustom: 7,
            vnrNameMatch: null,
            vnrOptions: []
        };
    },
    async created() {
        await this.getPharmacy();
        // get medication and its logs if vnr passed as routing parameter
        if (!this.error && this.drug.vnr) {
            await this.getMedicationName();
            if (!this.error) {
                await this.searchLogs();
            }
        }
        if (!this.error) {
            this.loaded = true;
        }
    },
    computed: {
        ...mapState('account', ['status', 'user', 'logout']),
        // string to be shown if there are no logs for the given search criteria
        noLogsString() {
            return `${this.$t('no_logs')} ${this.$t('current_inventory')} ${this.getMedDisplayAmount(
                this.currentInventory[0],
                this.currentInventory[1],
                false,
                'long'
            )}.`;
        }
    },
    methods: {
        // handle vnr selection (from dropdown)
        chooseVnr(vnr) {
            const medication = this.vnrOptions.find(medication => medication.vnr === vnr);
            this.vnrOptions = [];
            this.processMedication(medication);
            this.vnrNameMatch = true;
            this.searchDone = false;
        },
        // clear drug info
        clearDrug(preserveVnr) {
            const clearedDrug = {
                vnr: '',
                name: '',
                company: '',
                dosage: '',
                package_size: '',
                form: '',
                dosage_form: '',
                showName: '',
                showNamePrevious: ''
            };
            this.drug = preserveVnr ? { ...clearedDrug, vnr: this.drug.vnr } : clearedDrug;
            this.vnrNameMatch = null;
        },
        // clear vnr selection options
        clearVnrOptions() {
            this.vnrOptions = [];
        },
        // get formatted date value for date string
        getFileDate(dateString) {
            return dateString.length > 0 ? getLocalDateISOString(new Date(dateString)) : null;
        },
        // get appropriate layout preview image
        getLayoutImg(style) {
            return require(`@/assets/images/drug_card_layout__${style}__${this.$i18n.locale}.png`);
        },
        // get logs
        async getLogs(params = {}) {
            try {
                this.tableTitle = '';
                this.logsRetrieved = false;
                // add 1 day to searchTo to include current date
                let searchToPlus1 = '';
                if (params.searchTo.length > 0) {
                    searchToPlus1 = new Date(params.searchTo);
                    searchToPlus1.setDate(new Date(params.searchTo).getDate() + 1);
                    searchToPlus1 = searchToPlus1.toISOString().slice(0, 10);
                }

                const { logs, currentInventory } = await getDrugCard(this.user.pharmacy_id, {
                    params: { ...params, searchTo: searchToPlus1 }
                });

                this.logs = logs;
                this.currentInventory = currentInventory;

                if (!this.drug.showName || this.drug.showName === '') {
                    const medFromLog = this.logs[0]
                    this.drug = {
                        vnr: this.validateProdField(medFromLog.vnr),
                        supplier: this.validateProdField(medFromLog.supplier),
                        name: this.validateProdField(medFromLog.medication_name),
                        company: this.validateProdField(medFromLog.company),
                        dosage: this.validateProdField(medFromLog.dosage),
                        dosage_form: this.validateProdField(medFromLog.dosage_form),
                        form: this.validateProdField(medFromLog.form),
                        package_size: this.validateProdField(medFromLog.package_size)
                    };
                    this.drug.showName = this.getMedDisplayName(this.drug, false);
                    this.drug.showNamePrevious = this.drug.showName;
                }

                this.tableTitle = `${this.drug.vnr} ${this.drug.showName}`;
                this.logsRetrieved = true;
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // get medication info based on vnr
        async getMedicationName() {
            this.searchDone = false;
            this.clearDrug(true);
            if (this.drug.vnr.length === this.vnrLength || this.drug.vnr.length === this.vnrLengthCustom) {
                try {
                    const params = { searchVnr: this.drug.vnr, searchVnrExact: true };
                    const result = await getFilteredMedications({ params });
                    if (result.length) {
                        const medication = result[0];
                        this.processMedication(medication);
                        this.vnrNameMatch = true;
                    } else {
                        this.vnrNameMatch = false;
                    }
                } catch (error) {
                    handleAxiosError(error);
                    this.error = true;
                }
            }
        },
        // get possible medication vnrs based on med name input
        async getMedicationVnrs() {
            // if name not changed, do nothing
            if (this.drug.showName.trim() === this.drug.showNamePrevious) {
                return;
            }
            // clear previous vnr selection
            if (this.vnrNameMatch) {
                this.vnrNameMatch = null;
                this.drug.vnr = '';
            }
            // only proceed if at least three characters entered
            if (this.drug.showName.trim().length >= 3) {
                try {
                    this.searchingVnrs = true;
                    const params = { searchName: this.drug.showName.trim(), searchNameExact: false }; // allow partial name match
                    this.vnrOptions = await getFilteredMedications({ params });
                    this.searchDone = true;
                } catch (error) {
                    handleAxiosError(error);
                }
            } else if (this.drug.showName.trim().length === 0) {
                // if name field cleared, clear any existing vnr options
                this.searchDone = false;
                this.vnrOptions = [];
            }
            this.searchingVnrs = false;
        },
        // include delay in medication vnr search to prevent unnecessary queries
        getMedicationVnrsDelay() {
            clearTimeout(this.searchVnrTimer);
            this.searchVnrTimer = setTimeout(() => this.getMedicationVnrs(), this.searchVnrDelay);
        },
        // get pharmacy info
        async getPharmacy() {
            try {
                this.pharmacy = await getPharmacy(this.user.pharmacy_id);
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // format drug card print date
        getPrintDate(dateString) {
            return dateString.length > 0 ? new Date(dateString).toLocaleDateString('fi-FI') : null;
        },
        // handle amount column layout update
        async handleMedsColumnLayoutUpdate(value) {
            if (value !== this.pharmacy.config.drug_card_layout.individual_meds_separate_column) {
                this.pharmacy.config.drug_card_layout.individual_meds_separate_column = value;
                await this.updateDrugCardLayout();
            }
        },
        // create printable drug card pdf
        print() {
            const lineHeight = 1.4;
            const doc = new jsPDF({
                format: 'a4',
                unit: 'pt',
                orientation: 'landscape',
                putOnlyUsedFonts: true,
                lineHeight
            });

            const margin = 55;
            const marginHeaderFooter = 30;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let currentMargin = margin;

            const fontSizeBig = 15;
            const fontSizeNormal = 10;
            const fontSizeSmall = 9;

            let totalPagesExp = '{}';

            const fromDate = this.getPrintDate(this.searchParams.searchFrom);
            const toDate = this.getPrintDate(this.searchParams.searchTo);
            const fileFromDate = this.getFileDate(this.searchParams.searchFrom);
            const fileToDate = this.getFileDate(this.searchParams.searchTo);
            let pdfName = `${this.$t('drug_card')}__`;
            if (this.drug.vnr) {
                pdfName = `${pdfName}${this.drug.vnr}`;
                if (this.drug.name) pdfName = `${pdfName}_`;
            }
            if (this.drug.name) pdfName = `${pdfName}${this.drug.name}`;
            pdfName = `${pdfName}__${this.pharmacy.information.name}`;
            if (fromDate || toDate) {
                pdfName = `${pdfName}__${fromDate ? `${fileFromDate}_` : ''}–${toDate ? `_${fileToDate}` : ''}`;
            }
            pdfName = pdfName
                .replace(/( | )/g, '_')
                .replace(/('|´|\(|\))/g, '')
                .toLowerCase();
            doc.setProperties({ title: pdfName });

            // title
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(fontSizeBig);
            currentMargin = currentMargin + fontSizeBig - Math.floor(fontSizeBig / 4);
            let dosageAndPackageSize = '';
            if (this.drug.dosage !== '' && this.drug.package_size !== '' && this.drug.form !== '') {
                dosageAndPackageSize = ` (${this.drug.dosage}, ${this.drug.package_size} ${this.drug.form})`;
            } else if (this.drug.dosage !== '') dosageAndPackageSize = ` (${this.drug.dosage})`;
            else if (this.drug.package_size !== '' && this.drug.form !== '')
                dosageAndPackageSize = ` (${this.drug.package_size} ${this.drug.form})`;
            doc.text(margin, currentMargin, `${this.drug.vnr} ${this.drug.name}${dosageAndPackageSize}`);

            // additional drug info
            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(fontSizeNormal);
            currentMargin = currentMargin + fontSizeNormal * lineHeight + 2;
            let additionalInfo = [];
            // if (this.drug.vnr.length) additionalInfo.push(`${this.$t('vnr')}: ${this.drug.vnr}`);
            if (this.drug.company.length) {
                additionalInfo.push(`${this.$t('marketing_authorization_holder')}: ${this.drug.company}`);
            }
            if (this.drug.dosage.length) additionalInfo.push(`${this.$t('dosage')}: ${this.drug.dosage}`);
            if (this.drug.package_size.length && this.drug.form.length)
                additionalInfo.push(`${this.$t('package_size')}: ${this.drug.package_size} ${this.drug.form}`);
            if (this.drug.dosage_form.length)
                additionalInfo.push(`${this.$t('dosage_form')}: ${this.drug.dosage_form}`);
            doc.text(margin, currentMargin, additionalInfo);

            // additional drug card info
            let drugCardInfo = [];
            let timePeriod = '';
            if (!fromDate && !toDate) timePeriod = this.$t('all').toLowerCase();
            else timePeriod = `${fromDate ? fromDate : ''}–${toDate ? toDate : ''}`;
            drugCardInfo.push(`${this.$t(this.pharmacy.information.center_type)}: ${this.pharmacy.information.name}`);
            drugCardInfo.push(`${this.$t('time_period')}: ${timePeriod}`);
            const printDateString = getLocalDate('', this.$i18n.locale, true, false).replace(
                ' ',
                ` ${this.$t('time_at')} `
            );
            drugCardInfo.push(`${this.$t('print_date')}: ${printDateString}`);
            doc.text(pageWidth - margin, currentMargin, drugCardInfo, 'right');
            currentMargin =
                currentMargin +
                Math.max(additionalInfo.length, drugCardInfo.length) * fontSizeNormal * lineHeight +
                Math.floor(fontSizeNormal / 3);

            // column widths
            let columnStyles = {};
            const commentColStyle = { cellWidth: 100 };
            columnStyles[this.tableFields.length - 1] = commentColStyle;
            if (!this.pharmacy.config.drug_card_layout.individual_meds_separate_column) {
                const amountColStyle = { minCellWidth: 50 };
                columnStyles[2] = amountColStyle; // added
                columnStyles[3] = amountColStyle; // taken
                columnStyles[4] = amountColStyle; // inventory
            }

            // correction instructions
            doc.setFont('Helvetica', 'italic');
            doc.setFontSize(fontSizeSmall);
            doc.text(margin, currentMargin, this.$t('correction_legend'));
            doc.setFont('Helvetica', 'normal');
            currentMargin = currentMargin + fontSizeNormal * lineHeight - Math.floor(fontSizeNormal / 3);

            // table
            doc.setLineHeightFactor(1.15);
            if (document.getElementById('drugCardTable')) {
                doc.autoTable({
                    html: '#drugCardTable',
                    margin: margin,
                    startY: currentMargin,
                    rowPageBreak: 'avoid',
                    includeHiddenHtml: true,
                    columnStyles,
                    styles: { fontSize: fontSizeSmall },
                    didParseCell: data => {
                        if (data.section === 'head') {
                            const textContent = data.cell.raw.textContent.replace('\xa0(+', '\n(+');
                            data.cell.text = textContent.split('\n');
                        } else if (data.section === 'body' && (data.cell.raw.innerHTML.includes('resInfoCorrected') || data.cell.raw.innerHTML.includes('supplierCorrected'))) {
                            const span = document.createElement('span');
                            span.innerHTML = data.cell.raw.innerHTML;
                            const textArray = span.innerText.split('|||')[0].split('||'); // split original and corrected text values
                            // format corrected text
                            const correctedText = textArray[1]
                                .replace(/^ \[/, '- [')
                                .replace(/( \[)(?=[^\[]*$)/, '* [');
                            data.cell.text = `${correctedText} (${this.$t('original')}: ${textArray[0]})`;
                        }
                    },
                    didDrawPage: data => {
                        doc.setFontSize(fontSizeSmall);

                        // show header on subsequent pages
                        if (data.pageNumber > 1) {
                            let header = `${this.drug.vnr} ${this.drug.showName} (${this.pharmacy.information.name}`;
                            if (fromDate || toDate)
                                header = `${header}, ${fromDate ? fromDate : ''}–${toDate ? toDate : ' '}`;
                            header = `${header}, ${this.$t('printed').toLowerCase()} ${printDateString})`;
                            doc.text(pageWidth / 2, marginHeaderFooter, header, 'center');
                        }

                        // footer
                        const footer = `${this.$t('page')} ${data.pageNumber.toString()} / ${totalPagesExp}`;
                        doc.text(pageWidth / 2, pageHeight - marginHeaderFooter, footer, 'center');
                    }
                });
            } else {
                // "no logs" text
                doc.setFontSize(fontSizeNormal);
                currentMargin = currentMargin + fontSizeNormal * lineHeight + 2;
                doc.text(margin, currentMargin, this.noLogsString);
            }

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages(totalPagesExp);
            }

            doc.autoPrint();
            // doc.output('dataurlnewwindow');
            window.open(doc.output('bloburl'));
        },
        // process medication info fields
        processMedication(medication) {
            this.drug = {
                ...medication,
                name: this.validateProdField(medication.name),
                company: this.validateProdField(medication.company),
                dosage: this.validateProdField(medication.dosage),
                dosage_form: this.validateProdField(medication.dosage_form),
                form: this.validateProdField(medication.form),
                package_size: this.validateProdField(medication.package_size)
            };
            this.drug.showName = this.getMedDisplayName(this.drug, false);
            this.drug.showNamePrevious = this.drug.showName;
        },
        // reset search filters
        resetFilters() {
            this.clearDrug(false);
            this.logs = [];
            this.logsRetrieved = false;
            this.fromDate = '';
            this.toDate = '';
            this.searchParams.searchFrom = '';
            this.searchParams.searchTo = '';
            this.searchParams.searchVnr = '';
            this.tableFields = {};
            this.tableTitle = '';
        },
        // handle search click
        async searchLogs() {
            this.submitClicked = true;
            this.setSearchParams();
            await this.getLogs(this.searchParams);
            this.submitClicked = false;
        },
        // set search parameters for search
        setSearchParams() {
            this.searchParams.searchVnr = this.drug.vnr;
            this.searchParams.searchFrom = this.dateIsValid(this.fromDate) ? this.fromDate : '';
            this.searchParams.searchTo = this.dateIsValid(this.toDate) ? this.toDate : '';
        },
        // set selected vnr and get medication name
        setVNR(vnr) {
            this.drug.vnr = vnr;
            this.searchItems = [];
            this.searchDone = false;
            this.getMedicationName();
        },
        // update drug card layout (save selection in pharmacy config)
        async updateDrugCardLayout() {
            try {
                await updatePharmacy(this.user.pharmacy_id, { config: this.pharmacy.config });
                this.$toastr.s(this.$t('preferences_updated'));
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // validate medication info field
        validateProdField(field) {
            return field && field.toString().length > 0 ? field.toString() : '';
        }
    }
};
</script>

<style>
#contentselect {
    display: inline;
    width: 35%;
}
.custom_ul {
    border: 1px solid #ced4da;
    padding-left: 10px;
    list-style: none;
}
.custom_ul li {
    cursor: pointer;
    line-height: 30px;
}
.custom_ul li:hover {
    color: blue;
}
</style>
